import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconAddressBook, IconCopy, IconPencil, IconPlus, IconStar, IconTrash } from '@tabler/icons-react';
import ContactDialog, { type ContactDraft } from '@/components/app/company/ContactDialog';
import EmptyState from '@/components/common/EmptyState';
import RequisitesBlock from '@/components/common/RequisitesBlock';
import { useDeepLinkAction } from '@/hooks/useDeepLinkAction';
import { companyService } from '@/api/services/CompanyService';
import { getApiMessage, isNetworkError } from '@/api/errors';
import { COMPANY_CARD_QUERY_KEY, useCompanyCard } from '@/hooks/useCompanyCard';
import { useProfile } from '@/hooks/useProfile';
import { useIsManager } from '@/hooks/useRoles';
import { REQUIRED_DATA_QUERY_KEY } from '@/hooks/useRequiredData';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { formatPhone } from '@/lib/phone';
import { isBlank } from '@/lib/validators';
import type { CompanyCard as CompanyCardModel } from '@/types/company';
import { scrollBehavior } from '@/lib/motion';

const SITE_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i;
const NAME_RE = /^[а-яА-ЯёЁa-zA-Z .-]*$/;

interface FormValues {
  taxSystemId: string;
  kpp2: string;
  site: string;
  realAddress: string;
  postAddress: string;
  belongsToGroup: string;
  belongsEvidence: string;
  managementName: string;
  managementPost: string;
  contactPhones: ContactDraft[];
}

function toFormValues(card: CompanyCardModel): FormValues {
  return {
    taxSystemId: String(card.taxSystems.find((tax) => tax.isChecked)?.id ?? ''),
    kpp2: card.kpp2,
    site: card.site,
    realAddress: card.realAddress,
    postAddress: card.postAddress,
    belongsToGroup: card.belongsToGroup,
    belongsEvidence: card.belongsEvidence,
    managementName: card.managementName,
    managementPost: card.managementPost,
    contactPhones: card.contactPhones.map((contact) => ({
      id: contact.id,
      contactName: contact.contactName,
      phoneNumber: contact.phoneNumber,
      isMain: contact.isMain,
    })),
  };
}

// G1 — Карточка организации: реестр (read-only info-список) + редактируемые реквизиты/адреса/руководитель/контакты.
export default function CompanyCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: card, isLoading, isError } = useCompanyCard();
  const { data: profile } = useProfile();
  const [dialog, setDialog] = useState<{ opened: boolean; index: number | null }>({
    opened: false,
    index: null,
  });
  const location = useLocation();

  // Диплинк из баннера обязательных данных: ?addContact=true — сразу открыть добавление контакта.
  useDeepLinkAction('addContact', () => setDialog({ opened: true, index: null }));

  // Якорный скролл к секции карточки из баннера (#tax / #addresses / #management).
  // Зависит от card: запускается после загрузки данных, когда поля уже отрисованы.
  useEffect(() => {
    if (!card || !location.hash) {
      return;
    }
    const target = document.getElementById(location.hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      // Фокус ставим только обычным полям; combobox (Select) от фокуса раскрыл бы список.
      // При прямой загрузке по #хэшу браузер может сам сфокусировать combobox — снимаем фокус.
      const isCombobox = target.getAttribute('role') === 'combobox' || target.hasAttribute('aria-haspopup');
      if (isCombobox) {
        if (document.activeElement === target) {
          target.blur();
        }
      } else {
        target.focus({ preventScroll: true });
      }
    }
  }, [card, location.hash]);

  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: {
      taxSystemId: '',
      kpp2: '',
      site: '',
      realAddress: '',
      postAddress: '',
      belongsToGroup: '',
      belongsEvidence: '',
      managementName: '',
      managementPost: '',
      contactPhones: [],
    },
    validate: {
      taxSystemId: (value) => (isBlank(value) ? t('auth.validation.required') : null),
      kpp2: (value) => (value && !/^\d{1,9}$/.test(value) ? t('app.company.card.errKpp2') : null),
      site: (value) => (value && !SITE_RE.test(value) ? t('app.company.card.errSite') : null),
      managementName: (value) => (value && !NAME_RE.test(value) ? t('app.company.card.errName') : null),
    },
  });

  // Инициализация формы из загруженных данных + установка baseline для isDirty. НЕ перезаписываем
  // правки пользователя фоновым рефетчем (карта инвалидируется по SSE — src/realtime/useRealtime.ts):
  // синхронизируем только когда нет несохранённых правок — первая загрузка (форма ещё не dirty) и после
  // сохранения (resetDirty в onSuccess). Иначе правка тихо терялась бы, а гард ухода замолкал.
  useEffect(() => {
    if (card && !form.isDirty()) {
      const values = toFormValues(card);
      form.setValues(values);
      form.resetDirty(values);
    }
    // Зависимость только от card; form стабилен в рамках жизни компонента.
  }, [card]);

  const mutation = useMutation({
    mutationFn: () => {
      const values = form.getValues();
      return companyService.saveCompanyCard({
        site: values.site.trim(),
        postAddress: values.postAddress.trim(),
        realAddress: values.realAddress.trim(),
        belongsToGroup: values.belongsToGroup.trim(),
        managementName: values.managementName.trim(),
        managementPost: values.managementPost.trim(),
        belongsEvidence: values.belongsEvidence.trim(),
        kpp2: values.kpp2.trim(),
        contactPhones: values.contactPhones,
        taxSystems: values.taxSystemId ? [Number(values.taxSystemId)] : [],
      });
    },
    onSuccess: () => {
      form.resetDirty(form.getValues());
      queryClient.invalidateQueries({ queryKey: COMPANY_CARD_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: REQUIRED_DATA_QUERY_KEY }); // налог/контакт влияют на баннер
      notifications.show({ color: 'lime', message: t('app.profile.saved') });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: isNetworkError(error)
          ? t('auth.errors.generic')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
      });
    },
  });

  function handleSubmit(): void {
    if (form.validate().hasErrors) {
      return;
    }
    mutation.mutate();
  }

  // Изменять орг-данные может только владелец-менеджер (роль MANAGER); рядовой сотрудник видит карточку
  // только для чтения. Это удобство UI — доступ разграничивает бэкенд (иначе сотрудник получил бы 403).
  const canEdit = useIsManager();

  // Предупреждаем об уходе с несохранёнными правками карточки (вкл. переключение вкладок «Моя компания»
  // и клик по верхнему меню). После успешного сохранения form.resetDirty() гасит dirty — гард молчит.
  // Для сотрудника без права правки гард не нужен: сохранить он всё равно не может.
  useUnsavedChangesGuard(() => canEdit && form.isDirty());

  function handleContactSave(contact: ContactDraft): void {
    if (dialog.index === null) {
      form.insertListItem('contactPhones', contact);
    } else {
      form.setFieldValue(`contactPhones.${dialog.index}`, contact);
    }
    setDialog({ opened: false, index: null });
  }

  if (isError) {
    return (
      <Card withBorder padding="lg">
        <Text c="red">{t('auth.errors.generic')}</Text>
      </Card>
    );
  }

  if (isLoading || !card) {
    return (
      <Stack gap="sm">
        <Skeleton h={160} />
        <Skeleton h={240} />
      </Stack>
    );
  }

  const taxOptions = card.taxSystems.map((tax) => ({ value: String(tax.id), label: tax.name }));
  const okvedValue = [card.okved, card.okvedName].filter(Boolean).join(' — ');

  // Для самозанятого/ИП реестр содержит ОГРНИП (значение в том же поле ogrn), для юрлица — ОГРН.
  const ogrnLabel =
    profile?.type === 'SELF_EMPLOYED' ? t('app.company.card.ogrnip') : t('app.company.card.ogrn');

  const registryItems = [
    { label: t('auth.fields.inn'), value: card.inn, mono: true },
    { label: ogrnLabel, value: card.ogrn, mono: true },
    { label: t('app.company.card.okpo'), value: card.okpo, mono: true },
    { label: t('app.company.card.okato'), value: card.okato, mono: true },
    { label: t('app.company.card.okved'), value: okvedValue },
    { label: t('app.company.card.kpp'), value: card.kpp, mono: true },
    { label: t('app.company.card.regDate'), value: card.registrationDate },
    { label: t('app.company.card.regAddress'), value: card.registrationAddress },
  ];
  const dialogInitial =
    dialog.index !== null ? (form.values.contactPhones[dialog.index] ?? null) : null;

  return (
    <Stack gap="lg">
      <Card withBorder padding="lg">
        {/* Название организации в разделе не показываем, но добавляем в копию (copyLeadLines). */}
        <RequisitesBlock
          title={t('app.profile.fromRegistry')}
          items={registryItems}
          copyAllLabel={t('app.company.card.copyAll')}
          copyLeadLines={profile?.name ? [profile.name] : []}
        />
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={4}>{t('app.company.card.editableTitle')}</Title>

          {/* Сотруднику без права правки поля показываем только для чтения (disabled). */}
          <fieldset
            disabled={!canEdit}
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              minInlineSize: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--mantine-spacing-md)',
            }}
          >
          <Select
            id="tax"
            label={t('app.company.card.taxSystem')}
            placeholder={t('app.company.card.taxSystemPlaceholder')}
            data={taxOptions}
            withAsterisk
            allowDeselect={false}
            key={form.key('taxSystemId')}
            {...form.getInputProps('taxSystemId')}
          />

          <Group grow align="flex-start">
            <TextInput
              label={t('app.company.card.kpp2')}
              inputMode="numeric"
              maxLength={9}
              key={form.key('kpp2')}
              {...form.getInputProps('kpp2')}
            />
            <TextInput
              label={t('app.company.card.site')}
              placeholder="example.ru"
              key={form.key('site')}
              {...form.getInputProps('site')}
            />
          </Group>

          <TextInput
            id="addresses"
            label={t('app.company.card.realAddress')}
            rightSectionWidth={40}
            rightSection={
              card.registrationAddress ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={t('app.company.card.copyFromRegistry')}
                  onClick={() => form.setFieldValue('realAddress', card.registrationAddress ?? '')}
                >
                  <IconCopy size={18} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents="all"
            key={form.key('realAddress')}
            {...form.getInputProps('realAddress')}
          />
          <TextInput
            label={t('app.company.card.postAddress')}
            rightSectionWidth={40}
            rightSection={
              card.registrationAddress ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={t('app.company.card.copyFromRegistry')}
                  onClick={() => form.setFieldValue('postAddress', card.registrationAddress ?? '')}
                >
                  <IconCopy size={18} />
                </ActionIcon>
              ) : null
            }
            rightSectionPointerEvents="all"
            key={form.key('postAddress')}
            {...form.getInputProps('postAddress')}
          />

          <Group grow align="flex-start">
            <TextInput
              label={t('app.company.card.group')}
              key={form.key('belongsToGroup')}
              {...form.getInputProps('belongsToGroup')}
            />
            <TextInput
              label={t('app.company.card.groupEvidence')}
              key={form.key('belongsEvidence')}
              {...form.getInputProps('belongsEvidence')}
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              id="management"
              label={t('app.company.card.managementName')}
              key={form.key('managementName')}
              {...form.getInputProps('managementName')}
            />
            <TextInput
              label={t('app.company.card.managementPost')}
              key={form.key('managementPost')}
              {...form.getInputProps('managementPost')}
            />
          </Group>
          </fieldset>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>{t('app.company.card.contacts.title')}</Title>
            {canEdit && form.values.contactPhones.length > 0 ? (
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconPlus size={16} />}
                onClick={() => setDialog({ opened: true, index: null })}
              >
                {t('app.company.card.contacts.add')}
              </Button>
            ) : null}
          </Group>

          {form.values.contactPhones.length === 0 ? (
            <EmptyState
              decorated={false}
              icon={<IconAddressBook size={30} />}
              title={t('app.company.card.contacts.empty')}
              description={t('app.company.card.contacts.emptyHint')}
              action={
                canEdit ? (
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setDialog({ opened: true, index: null })}
                  >
                    {t('app.company.card.contacts.add')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            form.values.contactPhones.map((contact, index) => (
              <Group key={index} justify="space-between" wrap="nowrap">
                <div>
                  <Group gap="xs" wrap="nowrap">
                    <Text fw={500} size="sm">
                      {contact.contactName}
                    </Text>
                    {/* Основной контакт идёт в документы организации (contactName). */}
                    {contact.isMain ? (
                      <Badge size="sm" color="lime" variant="light">
                        {t('app.company.card.contacts.main')}
                      </Badge>
                    ) : null}
                  </Group>
                  <Text c="gray.7" size="sm">
                    {formatPhone(contact.phoneNumber)}
                  </Text>
                </div>
                {canEdit ? (
                  <Group gap={4} wrap="nowrap">
                    {!contact.isMain ? (
                      <Tooltip label={t('app.company.card.contacts.mainHint')} withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={t('app.company.card.contacts.makeMain')}
                          onClick={() =>
                            form.setFieldValue(
                              'contactPhones',
                              form.values.contactPhones.map((c, i) => ({ ...c, isMain: i === index })),
                            )
                          }
                        >
                          <IconStar size={18} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={t('app.company.card.contacts.edit')}
                      onClick={() => setDialog({ opened: true, index })}
                    >
                      <IconPencil size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('app.company.card.contacts.remove')}
                      onClick={() => {
                        const wasMain = contact.isMain;
                        const rest = form.values.contactPhones.filter((_, i) => i !== index);
                        // Удаляя основной контакт, назначаем основным первый из оставшихся: организация
                        // не должна остаться без основного (иначе пустой contactName в документах).
                        const first = rest[0];
                        if (wasMain && first) {
                          rest[0] = { ...first, isMain: true };
                        }
                        form.setFieldValue('contactPhones', rest);
                      }}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Group>
                ) : null}
              </Group>
            ))
          )}
        </Stack>
      </Card>

      {canEdit ? (
        <Group justify="flex-end">
          <Button onClick={handleSubmit} loading={mutation.isPending} disabled={!form.isDirty()}>
            {t('app.profile.save')}
          </Button>
        </Group>
      ) : null}

      <ContactDialog
        opened={dialog.opened}
        initial={dialogInitial}
        onSave={handleContactSave}
        onClose={() => setDialog({ opened: false, index: null })}
      />
    </Stack>
  );
}
